import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import type { WorkCardFieldColumn } from "@components/organizationView/clients-work/board/workCardFieldColumns";
import { getWorkCardFieldName } from "@components/organizationView/clients-work/board/WorkCardTag";
import type { BoardAddPlacement } from "@design-system/Board/Board.types";
import { TASK_BOARD_FIELDS, type TaskBoardField } from "./taskBoardActions";
import {
    DEFAULT_TASK_CARD_FIELD_PLACEMENTS,
    type TaskCardFieldPlacements,
} from "./taskBoardCardFields";
import { NO_STAGE_COLUMN_ID, TASK_STAGE_COLUMNS } from "./taskBoardConfig";

/** Which board the playground shows: the Tasks board's cards or the Work board's. */
export type BoardCardType = "tasks" | "work";

type WorkCardFieldId = WorkCardFieldColumn["id"];

/** The Work card fields the playground offers, in List's order, named as List names them. */
const WORK_CARD_FIELD_IDS: readonly WorkCardFieldId[] = [
    ClientsWorkOverviewColumn.SERVICE_PERIOD,
    ClientsWorkOverviewColumn.TIME_IN_STAGE,
    ClientsWorkOverviewColumn.WORK_DELIVERY_DATE,
    ClientsWorkOverviewColumn.TASK_PROGRESS,
    ClientsWorkOverviewColumn.APPLIED_BY,
    ClientsWorkOverviewColumn.HEALTH,
    ClientsWorkOverviewColumn.UNREAD_TASKS,
    ClientsWorkOverviewColumn.APPLICATION_TYPE,
    ClientsWorkOverviewColumn.APPLIED_AT,
];

export const WORK_CARD_FIELD_OPTIONS = WORK_CARD_FIELD_IDS.map((id) => ({
    id,
    label: getWorkCardFieldName(id),
}));

export interface BoardPlaygroundSettings {
    cardType: BoardCardType;
    /** The Work card's visible fields. */
    workCardFieldIds: readonly WorkCardFieldId[];
    addPlacementsByColumn: Readonly<
        Record<string, readonly BoardAddPlacement[]>
    >;
    isLockEnabled: boolean;
    isDeliverRuleEnabled: boolean;
    isManualOrder: boolean;
    /** About 1,000 generated tasks instead of the demo close. */
    isStressMode: boolean;
    /** The drawer of tasks an agent has in progress. */
    agentDrawer: AgentDrawerMode;
    cardFieldPlacements: TaskCardFieldPlacements;
    /** Fields with an inline editor that edit from the card; the rest read only. */
    editableCardFieldIds: ReadonlySet<TaskBoardField>;
}

export type AgentDrawerMode = "none" | "single" | "timeline";

export const AGENT_DRAWER_OPTIONS: ReadonlyArray<{
    value: AgentDrawerMode;
    label: string;
}> = [
    { value: "none", label: "None" },
    { value: "single", label: "Single item" },
    { value: "timeline", label: "Timeline" },
];

export const ADD_PLACEMENT_OPTIONS: ReadonlyArray<{
    value: BoardAddPlacement;
    label: string;
}> = [
    { value: "header", label: "Header" },
    { value: "footer", label: "Footer" },
    { value: "between", label: "Between cards" },
];

const getDefaultPlacements = (columnId: string): BoardAddPlacement[] => {
    if (columnId === NO_STAGE_COLUMN_ID) {
        return [];
    }
    return columnId === "prep"
        ? ["header", "footer", "between"]
        : ["header", "footer"];
};

/** Spec defaults: header and footer on every stage, between on Prep, none on No stage. */
export const DEFAULT_PLAYGROUND_SETTINGS: BoardPlaygroundSettings = {
    cardType: "tasks",
    workCardFieldIds: [
        ClientsWorkOverviewColumn.SERVICE_PERIOD,
        ClientsWorkOverviewColumn.TIME_IN_STAGE,
        ClientsWorkOverviewColumn.WORK_DELIVERY_DATE,
        ClientsWorkOverviewColumn.TASK_PROGRESS,
        ClientsWorkOverviewColumn.UNREAD_TASKS,
    ],
    addPlacementsByColumn: Object.fromEntries(
        TASK_STAGE_COLUMNS.map(({ id }) => [id, getDefaultPlacements(id)]),
    ),
    isLockEnabled: true,
    isDeliverRuleEnabled: true,
    isManualOrder: false,
    isStressMode: false,
    agentDrawer: "single",
    cardFieldPlacements: DEFAULT_TASK_CARD_FIELD_PLACEMENTS,
    editableCardFieldIds: new Set(TASK_BOARD_FIELDS),
};
